import { Graphics } from 'pixi.js';
import { drawCharacter } from './CharacterRenderer';
import { worldToScreen } from '../iso/IsoGrid';
import { findPath, type PathNode } from './Pathfinder';
import type { CharacterAppearance } from '../../core/types';
import { SeededRandom } from '../../core/rng/SeededRandom';
import { DESK_LAYOUT } from '../art/Furniture';

export type CharacterState = 'sitting' | 'idle' | 'walking';

/**
 * A single animated character in the office.
 * Manages position, state, movement, and drawing.
 */
export class CharacterSprite {
  appearance: CharacterAppearance;
  state: CharacterState = 'idle';

  // World tile position (can be fractional during movement)
  worldX: number;
  worldY: number;

  // Target desk (where they sit and work)
  deskX: number;
  deskY: number;

  // Walking
  private path: PathNode[] | null = null;
  private pathIndex: number = 0;
  private moveProgress: number = 0; // 0-1 between path nodes
  private moveSpeed: number = 0.03; // Tiles per frame

  // Animation
  private bobTimer: number = 0;
  private walkFrame: number = 0;

  // Wander timer (for idle characters who walk around)
  private wanderTimer: number = 0;
  private wanderInterval: number;
  private rng: SeededRandom;

  constructor(
    appearance: CharacterAppearance,
    deskX: number,
    deskY: number,
    rng: SeededRandom,
    wanderInterval: number = 300,
  ) {
    this.appearance = appearance;
    this.deskX = deskX;
    this.deskY = deskY;
    this.rng = rng;
    // Sit on the chair at the back of the desk tile
    this.worldX = deskX;
    this.worldY = deskY + DESK_LAYOUT.seatOffsetY;
    this.state = 'sitting';
    this.wanderInterval = wanderInterval + rng.nextInt(0, 200);
  }

  /**
   * Send the character walking to a target tile.
   */
  walkTo(
    targetX: number,
    targetY: number,
    gridWidth: number,
    gridHeight: number,
    blocked: Set<string>,
  ): boolean {
    const sx = Math.round(this.worldX);
    const sy = Math.round(this.worldY);
    const path = findPath(sx, sy, targetX, targetY, gridWidth, gridHeight, blocked);
    if (!path || path.length < 2) return false;

    this.path = path;
    this.pathIndex = 0;
    this.moveProgress = 0;
    this.state = 'walking';
    return true;
  }

  /** Send the character back to their desk. */
  returnToDesk(gridWidth: number, gridHeight: number, blocked: Set<string>): void {
    if (this.state === 'sitting') return;
    // Walk to a tile adjacent to the desk, then snap to chair position
    // Try the tile behind the desk first (back = y-1), then sides
    const adjacent = [
      { x: this.deskX, y: this.deskY - 1 },
      { x: this.deskX - 1, y: this.deskY },
      { x: this.deskX + 1, y: this.deskY },
    ];
    for (const target of adjacent) {
      if (target.x < 0 || target.y < 0 || target.x >= gridWidth || target.y >= gridHeight) continue;
      if (blocked.has(`${target.x},${target.y}`)) continue;
      if (this.walkTo(target.x, target.y, gridWidth, gridHeight, blocked)) return;
    }
    // Can't find path, teleport
    this.worldX = this.deskX;
    this.worldY = this.deskY + DESK_LAYOUT.seatOffsetY;
    this.state = 'sitting';
  }

  /**
   * Update the character each frame.
   * @param gridWidth, gridHeight - Grid dimensions for pathfinding
   * @param blocked - Blocked tiles
   * @param allowWander - Whether idle characters should randomly walk around
   */
  update(
    gridWidth: number,
    gridHeight: number,
    blocked: Set<string>,
    allowWander: boolean = true,
  ): void {
    this.bobTimer += 0.05;
    this.walkFrame++;

    if (this.state === 'walking') {
      this.updateWalking();
    } else if (this.state === 'idle' && allowWander) {
      this.wanderTimer++;
      if (this.wanderTimer >= this.wanderInterval) {
        this.wanderTimer = 0;
        // Pick a random nearby walkable tile
        const rx = Math.round(this.worldX) + this.rng.nextInt(-2, 2);
        const ry = Math.round(this.worldY) + this.rng.nextInt(-2, 2);
        const cx = Math.max(0, Math.min(gridWidth - 1, rx));
        const cy = Math.max(0, Math.min(gridHeight - 1, ry));
        if (!blocked.has(`${cx},${cy}`)) {
          this.walkTo(cx, cy, gridWidth, gridHeight, blocked);
        }
      }
    }
  }

  private updateWalking(): void {
    if (!this.path || this.pathIndex >= this.path.length - 1) {
      // Arrived
      this.path = null;
      // Check if we're at our desk
      const atDesk =
        Math.abs(this.worldX - this.deskX) < 0.5 &&
        Math.abs(this.worldY - (this.deskY + DESK_LAYOUT.seatOffsetY)) < 0.5;
      this.state = atDesk ? 'sitting' : 'idle';
      return;
    }

    this.moveProgress += this.moveSpeed;

    if (this.moveProgress >= 1) {
      this.pathIndex++;
      this.moveProgress = 0;

      if (this.pathIndex >= this.path.length - 1) {
        // Snap to final position
        const last = this.path[this.path.length - 1]!;
        this.worldX = last.x;
        this.worldY = last.y;
        const atDesk =
          Math.abs(this.worldX - this.deskX) < 1.5 &&
          Math.abs(this.worldY - (this.deskY + DESK_LAYOUT.seatOffsetY)) < 0.5;
        this.state = atDesk ? 'sitting' : 'idle';
        this.path = null;
        return;
      }
    }

    // Interpolate between current and next path node
    const from = this.path[this.pathIndex]!;
    const to = this.path[this.pathIndex + 1]!;
    this.worldX = from.x + (to.x - from.x) * this.moveProgress;
    this.worldY = from.y + (to.y - from.y) * this.moveProgress;
  }

  /** Draw the character. */
  draw(g: Graphics): void {
    const screen = worldToScreen(this.worldX, this.worldY);
    let drawState: 'idle' | 'working' | 'walking';

    if (this.state === 'sitting') {
      drawState = 'working';
    } else if (this.state === 'walking') {
      drawState = 'walking';
    } else {
      drawState = 'idle';
    }

    // Animation offsets by state
    let yOffset = 0;
    if (this.state === 'walking') {
      yOffset = Math.sin(this.walkFrame * 0.3) * 1.5;
    } else if (this.state === 'sitting') {
      // Subtle breathing + occasional head bob
      yOffset = Math.sin(this.bobTimer) * 0.3;
      if (Math.sin(this.bobTimer * 0.1) > 0.95) yOffset -= 1; // Occasional nod
    } else {
      // Idle: gentle sway + occasional stretch
      yOffset = Math.sin(this.bobTimer * 0.5) * 0.5;
      if (Math.sin(this.bobTimer * 0.07) > 0.97) yOffset -= 2; // Stretch up
    }

    drawCharacter(g, screen.x, screen.y + yOffset, this.appearance, drawState);
  }

  /** Screen position for depth sorting. */
  get screenY(): number {
    return worldToScreen(this.worldX, this.worldY).y;
  }
}
